import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { UserRole } from '@/types';
import { useOidcAuth } from '@/hooks/useOidcAuth';
import { isOidcEnabled, isOidcEnforced } from '@/auth/oidcClient';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles,
}) => {
  const { isAuthenticated, user } = useAuthStore();
  const oidc = useOidcAuth();
  const oidcEnabled = isOidcEnabled();
  const oidcEnforced = isOidcEnforced();
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  // Track verification state to avoid redundant checks
  const lastAuthStateRef = useRef<string | null>(null);

  // Reset stale auth ref when user logs out so a subsequent login is not skipped
  useEffect(() => {
    if (!isAuthenticated) {
      lastAuthStateRef.current = '';
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Create a stable auth state key to detect actual changes
    const authStateKey = oidcEnabled
      ? `oidc:${oidc.isAuthenticated}:${oidc.isLoading}`
      : `local:${isAuthenticated}`;

    // Skip if OIDC is still loading
    if (oidcEnabled && oidc.isLoading) {
      setIsLoading(true);
      return;
    }

    // Check if auth state actually changed
    if (lastAuthStateRef.current === authStateKey) {
      return;
    }
    lastAuthStateRef.current = authStateKey;

    // Set loading to false - we've determined auth state
    // User data sync is handled by OidcSessionSync component
    setIsLoading(false);

  }, [oidcEnabled, oidc.isAuthenticated, oidc.isLoading, isAuthenticated]);

  // Determine if user is authenticated
  const hasAuth = oidcEnabled ? oidc.isAuthenticated : isAuthenticated;

  // Show loading state during auth check
  if (isLoading || (oidcEnabled && oidc.isLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 mm-light mm-light-bg text-gray-900 dark:mm-workplace dark:mm-workplace-bg dark:text-white">
        <div className="mm-glass rounded-3xl p-8 text-center shadow-2xl">
          <div className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-white/20 border-t-violet-400"></div>
          <p className="text-sm text-white/70">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!hasAuth) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // For OIDC mode, wait for user data to be synced by OidcSessionSync
  // This prevents flash of redirect while user data is being fetched
  if (oidcEnabled && !user) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 mm-light mm-light-bg text-gray-900 dark:mm-workplace dark:mm-workplace-bg dark:text-white">
        <div className="mm-glass rounded-3xl p-8 text-center shadow-2xl">
          <div className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-white/20 border-t-violet-400"></div>
          <p className="text-sm text-white/70">Loading user data...</p>
        </div>
      </div>
    );
  }

  // 2FA check takes precedence over role checks -- redirect immediately
  // so that admins/managers complete 2FA setup before any role gate fires
  if (user && !oidcEnforced && location.pathname !== '/2fa-setup') {
    if (
      (user.role === 'admin' || user.role === 'manager') &&
      user.is_2fa_enabled === false
    ) {
      return <Navigate to="/2fa-setup" state={{ from: location }} replace />;
    }
  }

  // Check role-based access (only reached after 2FA check passes)
  if (requiredRoles && requiredRoles.length > 0 && user) {
    if (!requiredRoles.includes(user.role)) {
      return <Navigate to="/access-denied" state={{ requiredRoles, userRole: user.role, from: location }} replace />;
    }
  }

  return <>{children}</>;
};
